// src/views/SingleProduct.jsx
import { useState, useEffect, useRef, lazy, Suspense, useCallback, useMemo } from "react";
import { formatCategory, openLinkInNewTab, consoleDebug, getPrioritySpecialForProduct } from "../util";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import useFetchRest from "../hooks/useFetchRest";
import { didSurfsideLoad, trackSurfsideEvent } from "../components/Surfside/util";
import LoadingSpinner from "../components/LoadingSpinner";
import SpecialDetails from "../components/SingleProduct/SpecialDetails";
import { ErrorBoundary } from "@sentry/react";
import ErrorComponent from "../ErrorComponent";
import { useConfig } from "../contexts/ConfigContext";
import { useAppState } from '../contexts/AppStateContext';
import { useSingleProduct, useSpecialsList } from "../hooks/dutchie-queries";
import { gtmTrackEvent } from '../analytics.js';

// Lazy-loaded components
const ProductImage = lazy(() => import("../components/SingleProduct/ProductImage"));
const Category = lazy(() => import("../components/SingleProduct/Category"));
const AddToCart = lazy(() => import("../components/SingleProduct/AddToCart"));
const MobileNav = lazy(() => import("../components/SingleProduct/MobileNav"));
const Effects = lazy(() => import("../components/SingleProduct/Effects"));
const Cannabinoids = lazy(() => import("../components/SingleProduct/Cannabinoids"));
const Terpenes = lazy(() => import("../components/SingleProduct/Terpenes"));
const Surfside = lazy(() => import("../components/Surfside/Surfside"));
const PotencyChart = lazy(() => import("../components/SingleProduct/PotencyChart"));
const ProductsGrid = lazy(() => import("../components/ProductsView/ProductsGrid"));

const SingleProduct = () => {
    const params = useParams();
    const navigate = useNavigate();
    const { state } = useLocation();
    const {
        retailers,
        surfside,
        analytics,
        singleProductOptions,
        debug_mode,
    } = useConfig();

    const { retailerId, productPopupData, setProductPopupData } = useAppState();

    const [selectedVariant, setSelectedVariant] = useState(null);
    const [surfsideTracked, setSurfsideTracked] = useState(false);
    const [surfsideLocationId] = useState(null);
    const [prioritySpecial, setPrioritySpecial] = useState(null);
    const [allSpecials, setAllSpecials] = useState([]);
    const [specialProductsIds, setSpecialProductsIds] = useState(null);

    const hasTrackedViewItem = useRef(false);
    const dialogRef = useRef(null);

    // Fetch product data
    const { data: fetchedProduct, isFetched: isFetchedProduct } = useSingleProduct(
        retailerId,
        params.categoryName,
        {
            enabled: !productPopupData && !state?.product,
        }
    );

    // Fetch menu data
    const { data: menuData, isFetched: isFetchedMenuData } = useFetchRest(
        `data/menu?retailerId=${retailerId}`,
        {
            enabled: !!retailerId
        }
    );

    // Compute the product directly
    const product = useMemo(() => {
        return productPopupData || state?.product || fetchedProduct || null;
    }, [productPopupData, state?.product, fetchedProduct]);

    // Fetch specials list
    const { data: specialsList, isFetched: isFetchedSpecials } = useSpecialsList(retailerId);

    // Fetch product optimization description (only when product is available)
    const { data: productOpDesc } = useFetchRest(
        `productop/description/${retailerId}/${product?.id}`,
        { enabled: !!product && !!retailers[retailerId].productOp }
    );

    const setTitleAndSEO = useCallback((product) => {
        const retailerFormatted = retailers[retailerId].retailerName.replace(/[()]/g, " - ");
        document.title = `${product.name} | Shop ${formatCategory(product.category)} | ${retailerFormatted}`;

        const metaDescription = `Offered at ${retailerFormatted}, try ${product.name}${product.brand?.name ? ` from ${product.brand.name}` : ''} today.`;
        const metaDescriptionTag = document.querySelector('meta[name="description"]');
        if (metaDescriptionTag) {
            metaDescriptionTag.setAttribute("content", metaDescription);
        }
    }, [retailerId, retailers]);

    const continueShopping = useCallback(() => {
        if (productPopupData) {
            navigate(-1);
            setProductPopupData(false);
        } else if (window.history.length >= 2 && document.referrer.includes(window.location.hostname)) {
            navigate(-1);
        } else if (retailers[retailerId].menuSlug && params?.viewName === "shop") {
            navigate("/shop/" + retailers[retailerId].menuSlug);
        } else {
            navigate(window.location.hostname);
        }
    }, [productPopupData, navigate, setProductPopupData, retailerId, params, retailers]);

    // Set product data and initial tracking
    useEffect(() => {
        if (!product) return;

        setTitleAndSEO(product);

        consoleDebug('📦 Product Data Loaded:', {
            id: product.id,
            name: product.name,
            category: product.category,
            variants: product.variants?.length || 0,
            brand: product.brand?.name || 'No brand',
            fullObject: product
        });

        // Set initial variant
        if (product.variants?.length > 0 && !selectedVariant) {
            setSelectedVariant(product.variants[0]);
        }

        // Track Surfside initial view
        if (!surfsideTracked && surfside?.enable && product.variants?.[0]) {
            didSurfsideLoad(() => {
                trackSurfsideEvent(
                    "detail",
                    product,
                    product.variants[0],
                    1,
                    null,
                    retailerId,
                    retailers[retailerId]
                );
                setSurfsideTracked(true);
            });
        }
    }, [product, selectedVariant, surfsideTracked, setTitleAndSEO, retailerId, surfside, retailers]);

    // Preload product image for LCP
    useEffect(() => {
        if (!product?.images?.[0]?.url) return;

        const imageUrl = product.images[0].url;
        const supportsWebP = imageUrl.includes('dutchie.com');
        const preloadUrl = supportsWebP ? `${imageUrl}?w=800&fm=webp` : `${imageUrl}?w=800`;

        // Check if preload already exists
        const existingPreload = document.querySelector(`link[rel="preload"][href="${preloadUrl}"]`);
        if (existingPreload) return;

        // Create and add preload link
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = preloadUrl;
        link.type = supportsWebP ? 'image/webp' : 'image/jpeg';
        link.fetchPriority = 'high';

        // Note: imageSrcset and imageSizes might not be needed for the preload
        // The main preload URL is what matters most

        document.head.appendChild(link);

        // Cleanup on unmount or URL change
        return () => {
            if (document.head.contains(link)) {
                document.head.removeChild(link);
            }
        };
    }, [product]);

    // Accessibility: Tab key handling
    const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const handleTabKey = (e) => {
        if (!dialogRef.current || !productPopupData) return;

        const focusableContent = dialogRef.current.querySelectorAll(focusableElements);
        const firstFocusable = focusableContent[0];
        const lastFocusable = focusableContent[focusableContent.length - 1];

        if (e.key === 'Tab') {
            if (e.shiftKey) {
                if (document.activeElement === firstFocusable) {
                    e.preventDefault();
                    lastFocusable.focus();
                }
            } else {
                if (document.activeElement === lastFocusable) {
                    e.preventDefault();
                    firstFocusable.focus();
                }
            }
        }
    };

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape' && productPopupData) {
            continueShopping();
        }
        handleTabKey(e);
    }, [productPopupData, continueShopping]);

    // Set up focus management when popup opens/closes
    useEffect(() => {
        if (productPopupData) {
            setTimeout(() => {
                if (dialogRef.current) {
                    dialogRef.current.focus();
                }
            }, 100);

            document.body.style.overflow = 'hidden';
            document.getElementById('root')?.setAttribute('aria-hidden', 'true');
        } else {
            if (window.lastFocusedElement) {
                setTimeout(() => {
                    window.lastFocusedElement.focus();
                    window.lastFocusedElement = null;
                }, 0);
            }

            document.body.style.overflow = '';
            document.getElementById('root')?.removeAttribute('aria-hidden');
        }

        return () => {
            document.body.style.overflow = '';
            document.getElementById('root')?.removeAttribute('aria-hidden');

            if (productPopupData && window.lastFocusedElement) {
                window.lastFocusedElement.focus();
                window.lastFocusedElement = null;
            }
        };
    }, [productPopupData]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // Get specialProductsIds from either context or menuData
    useEffect(() => {
        if (productPopupData?.specialProductsIds) {
            setSpecialProductsIds(productPopupData.specialProductsIds);
        } else if (menuData?.specialProductsIds) {
            setSpecialProductsIds(menuData.specialProductsIds);
        }
    }, [productPopupData, menuData]);

    // Find priority special and all specials for product
    useEffect(() => {
        if (!product?.id || !specialsList || !specialProductsIds) return;

        // Find priority special
        const prioritySpecial = getPrioritySpecialForProduct(
            product.id,
            specialsList,
            specialProductsIds
        );
        setPrioritySpecial(prioritySpecial);

        // Find all specials for this product
        const matchingSpecialIds = [];
        for (const specialId in specialProductsIds) {
            if (specialProductsIds[specialId]?.includes(product.id)) {
                matchingSpecialIds.push(specialId);
            }
        }

        const matchingSpecials = specialsList.filter(special =>
            matchingSpecialIds.includes(special.id)
        );

        // Sort by priority (newest first)
        matchingSpecials.sort((a, b) => {
            const aStart = a?.scheduleConfiguration?.startStamp ? new Date(a.scheduleConfiguration.startStamp) : null;
            const bStart = b?.scheduleConfiguration?.startStamp ? new Date(b.scheduleConfiguration.startStamp) : null;

            if (!aStart && !bStart) return 0;
            if (!aStart) return 1;
            if (!bStart) return -1;

            return bStart - aStart;
        });

        setAllSpecials(matchingSpecials);

        consoleDebug('🎯 Specials for Product:', {
            productId: product.id,
            prioritySpecial: prioritySpecial,
            allSpecials: matchingSpecials
        });
    }, [product, specialsList, specialProductsIds]);

    // Track Surfside with priority special (when available)
    useEffect(() => {
        if (!surfsideTracked && surfside?.enable && product && selectedVariant && prioritySpecial !== undefined) {
            didSurfsideLoad(() => {
                trackSurfsideEvent(
                    "detail",
                    product,
                    selectedVariant,
                    1,
                    prioritySpecial,
                    retailerId,
                    retailers[retailerId]
                );
                setSurfsideTracked(true);
            });
        }
    }, [product, selectedVariant, prioritySpecial, surfsideTracked, retailerId, surfside, retailers]);

    // Track GTM event once product and variant are ready
    useEffect(() => {
        if (!hasTrackedViewItem.current && product && selectedVariant && analytics?.gtm_id) {
            gtmTrackEvent(
                'view_item',
                product,
                selectedVariant,
                1,
                prioritySpecial,
            );
            hasTrackedViewItem.current = true;
        }
    }, [product, selectedVariant, prioritySpecial, analytics?.gtm_id]);

    // Memoize filter object to prevent infinite re-renders in related products
    const relatedProductsFilter = useMemo(() => ({
        category: product?.category,
        brandIds: product?.brand?.id ? [product.brand.id] : [],
        strainType: product?.strainType,
    }), [product?.category, product?.brand?.id, product?.strainType]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!productPopupData || !dialogRef.current) return;

            let isInsidePopup = false;
            let checkElement = event.target;

            while (checkElement && checkElement !== document) {
                if (checkElement.classList.contains('single-product-pop') ||
                    checkElement.id === 'singleProductPop') {
                    isInsidePopup = true;
                    break;
                }
                checkElement = checkElement.parentElement;
            }

            if (!isInsidePopup) {
                continueShopping();
            }
        };

        if (productPopupData) {
            document.addEventListener("mousedown", handleClickOutside, true);
        }

        return () => document.removeEventListener("mousedown", handleClickOutside, true);
    }, [productPopupData, continueShopping]);

    // Redirect if product not found
    useEffect(() => {
        if (isFetchedProduct && !fetchedProduct && params?.locationSlug) {
            window.location.href = `/shop/${params.locationSlug}/`;
            return;
        }
    }, [isFetchedProduct, fetchedProduct, params?.locationSlug]);

    // Comprehensive ready check - ensure all critical data is loaded
    const isProductReady = useMemo(() => {
        // Must have product data
        if (!product) {
            return false;
        }

        // Must have menu data (needed for specials metadata)
        if (!isFetchedMenuData) {
            return false;
        }

        // Must have specials list loaded (even if empty)
        if (!isFetchedSpecials) {
            return false;
        }

        return true;
    }, [product, isFetchedMenuData, isFetchedSpecials]);

    // Show simplified loading spinner until all data is ready
    if (!isProductReady) {
        return <LoadingSpinner loadingType='menuList' />;
    }

    // Render the full product view once all data is available
    return (
        <div
            id="singleProductPop"
            className={productPopupData || state?.product ? "single-product-pop" : "single-product-static"}
            ref={dialogRef}
            role={productPopupData ? "dialog" : "main"}
            aria-label={productPopupData ? "Product details popup" : "Product details"}
            aria-modal={productPopupData ? "true" : undefined}
            onKeyDown={handleKeyDown}
            tabIndex={productPopupData ? "0" : undefined}
        >
            <Suspense fallback={null}>
                <MobileNav
                    imageUrl={product.images[0].url}
                    variants={product.variants}
                    imageAlt={`${product.name} image`}
                />

                {surfside?.enable && surfside.showProductBanner && (
                    <Surfside
                        placement="product"
                        accountId={surfside.account_id}
                        sourceId={surfside.source_id}
                        placementId={surfside.placement_ids.single_product}
                        locationId={surfsideLocationId}
                    />
                )}

                <div className="range-headless headless-single-product" id="singleProduct">
                    <nav className="headless-single-product-back" aria-label="Product navigation">
                        <button
                            className="headless-back-link"
                            onClick={continueShopping}
                            aria-label="Continue shopping"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                height="1em"
                                viewBox="0 0 448 512"
                                aria-hidden="true"
                                focusable="false"
                            >
                                <path d="M7.4 273.4C2.7 268.8 0 262.6 0 256s2.7-12.8 7.4-17.4l176-168c9.6-9.2 24.8-8.8 33.9 .8s8.8 24.8-.8 33.9L83.9 232 424 232c13.3 0 24 10.7 24 24s-10.7 24-24 24L83.9 280 216.6 406.6c9.6 9.2 9.9 24.3 .8 33.9s-24.3 9.9-33.9 .8l-176-168z" />
                            </svg>
                            <span>Continue shopping</span>
                        </button>
                        {(productPopupData || state?.product) && (
                            <div className="single-product-pop-controls" role="toolbar" aria-label="Product popup controls">
                                <button
                                    className="single-product-copy"
                                    onClick={() => openLinkInNewTab()}
                                    aria-label="Open in new tab"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 640 512"
                                        aria-hidden="true"
                                        focusable="false"
                                    >
                                        <path d="M143.9 64C64.4 64 0 128.4 0 207.9c0 75.7 58.7 138.5 134.3 143.5l8.1 .5c13.2 .9 24.7-9.1 25.5-22.3s-9.1-24.7-22.4-25.5l-8.1-.5C87.1 300.2 48 258.3 48 207.9c0-52.9 42.9-95.9 95.9-95.9H304.1c52.9 0 95.9 42.9 95.9 95.9c0 50.5-39.1 92.3-89.5 95.6l-8.1 .5c-13.2 .9-23.2 12.3-22.4 25.5s12.3 23.2 25.5 22.3l8.1-.5c75.6-5 134.3-67.8 134.3-143.5C448 128.4 383.6 64 304.1 64H143.9zM496.1 448C575.6 448 640 383.6 640 304.1c0-75.7-58.7-138.5-134.3-143.5l-8.1-.5c-13.2-.9-24.7 9.1-25.5 22.4s9.1 24.7 22.3 25.5l8.1 .5c50.4 3.4 89.5 45.2 89.5 95.6c0 52.9-42.9 95.9-95.9 95.9H335.9c-53 0-95.9-42.9-95.9-95.9c0-50.5 39.1-92.3 89.5-95.7l7.1-.5c13.2-.9 23.2-12.3 22.3-25.6s-12.3-23.2-25.6-22.3l-7.1 .5C250.7 165.6 192 228.4 192 304.1C192 383.6 256.4 448 335.9 448H496.1z" />
                                    </svg>
                                </button>
                                <button
                                    className="single-product-pop-close"
                                    onClick={continueShopping}
                                    aria-label="Close product details"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 384 512"
                                        aria-hidden="true"
                                        focusable="false"
                                    >
                                        <path d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z" />
                                    </svg>
                                </button>
                            </div>
                        )}
                    </nav>

                    <div className="row">
                        <ProductImage url={product.images[0].url} alt={`${product.name} product image`} />

                        <div className="col-lg-6 col-xxl-7">
                            <article className="headless-sp-primary" aria-labelledby="product-title">
                                <div className="headless-single-product-cls-wrap">
                                    <Category category={product.category} strainType={product.strainType} />

                                    <h1 id="product-title" className="headless-sp-title pe-lg-5">{product.name}</h1>

                                    {product.brand && (
                                        <div className="headless-sp-brand">
                                            <span>By </span>
                                            <a
                                                href={`/shop/${retailers[retailerId].menuSlug}/brand/${product.brand.name.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-').toLowerCase()}/`}
                                                aria-label={`View all products by ${product.brand.name}`}
                                            >
                                                {product.brand.name}
                                            </a>
                                        </div>
                                    )}

                                    <AddToCart
                                        product={product}
                                        variants={product.variants}
                                        specialData={prioritySpecial}
                                        selectedVariant={selectedVariant}
                                        setSelectedVariant={setSelectedVariant}
                                    />

                                    {(prioritySpecial || allSpecials.length > 0) && (
                                        <SpecialDetails
                                            specialData={prioritySpecial}
                                            allSpecials={allSpecials}
                                            menuSlug={retailers[retailerId]?.menuSlug}
                                        />
                                    )}
                                </div>

                                {singleProductOptions.showProductDescription && (
                                    <section className="headless-sp-description" aria-labelledby="description-heading">
                                        <h2 id="description-heading" className="headless-breadcrumb">Product Description</h2>
                                        <div className="headless-sp-content">
                                            {retailers[retailerId].productOp && productOpDesc ? (
                                                productOpDesc.split("\n").map((str, index) =>
                                                    <p key={index}>{str}</p>
                                                )
                                            ) : (
                                                singleProductOptions.showProductDescriptionHtml &&
                                                    product.descriptionHtml ? (
                                                    <div dangerouslySetInnerHTML={{ __html: product.descriptionHtml }} />
                                                ) : (
                                                    product.description
                                                )
                                            )}
                                        </div>
                                    </section>
                                )}

                                {singleProductOptions.showProductEffects && (
                                    <Effects effects={product.effects} />
                                )}

                                {singleProductOptions.showPotencyChart && (
                                    <PotencyChart potencyThc={product.potencyThc} potencyCbd={product.potencyCbd} />
                                )}

                                {singleProductOptions.showCannabinoids && product.cannabinoids && (
                                    <Cannabinoids cannabinoids={product.cannabinoids} />
                                )}

                                {singleProductOptions.showTerpenes && product.terpenes &&
                                    product.terpenes.length > 0 &&
                                    product.terpenes.some(t => t.value > 0) && (
                                        <Terpenes terpenes={product.terpenes} showInfo={singleProductOptions.showTerpenesInfo} />
                                    )}
                            </article>
                        </div>
                    </div>
                </div>

                {singleProductOptions.showRelatedProducts && (
                    <section aria-labelledby="related-products-heading">
                        <h2 id="related-products-heading" className="headless-shortcode-title headless-related-products-title">
                            You may also like
                        </h2>
                        <ErrorBoundary
                            showDialog
                            fallback={(props) => <ErrorComponent {...props} />}
                            beforeCapture={(scope) => {
                                scope.setTag("component", "ProductsGrid");
                                scope.setTag("parent", "SingleProduct");
                            }}
                        >
                            <div id="related-products-grid">
                                <ProductsGrid
                                    filter={relatedProductsFilter}
                                    menuSlug={params.locationSlug}
                                    rootId="related-products-grid"
                                    atts={{
                                        title: false,
                                        slider: true,
                                        numProducts: 12,
                                    }}
                                    isRelatedProducts={true}
                                />
                            </div>
                        </ErrorBoundary>
                    </section>
                )}
            </Suspense>
        </div>
    );
};

export default SingleProduct;
